/**
 * Bot Status Chart Component
 * Displays historical bot status snapshots (wallet balance, offers, loans) using Recharts
 */
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { BotStatusSnapshot } from '../services/bots.service'
import { formatCurrency } from '../utils/formatters'

interface BotStatusChartProps {
  snapshots: BotStatusSnapshot[]
  currency: string
  timeRange: number // hours
  onTimeRangeChange: (hours: number) => void
}

interface ChartDataPoint {
  timestamp: number
  dateLabel: string
  wallet_balance: number
  active_offers: number
  active_loans: number
  total_earned: number
}

export default function BotStatusChart({
  snapshots,
  currency,
  timeRange,
  onTimeRangeChange,
}: BotStatusChartProps) {
  // Transform snapshots into chart data
  const chartData: ChartDataPoint[] = snapshots
    .map((snapshot) => ({
      timestamp: snapshot.created_at,
      dateLabel: formatTimestamp(snapshot.created_at),
      wallet_balance: snapshot.wallet_balance,
      active_offers: snapshot.active_offers,
      active_loans: snapshot.active_loans,
      total_earned: snapshot.total_earned,
    }))
    .reverse() // Oldest first for chronological display

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload as ChartDataPoint
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipTitle}>{point.dateLabel}</p>
          <div style={styles.tooltipContent}>
            <p style={styles.tooltipItem}>
              錢包餘額: <span style={{ ...styles.tooltipValue, color: '#2563eb' }}>
                {formatCurrency(point.wallet_balance, currency)}
              </span>
            </p>
            <p style={styles.tooltipItem}>
              掛單數: <span style={{ ...styles.tooltipValue, color: '#8b5cf6' }}>
                {point.active_offers}
              </span>
            </p>
            <p style={styles.tooltipItem}>
              貸款數: <span style={{ ...styles.tooltipValue, color: '#10b981' }}>
                {point.active_loans}
              </span>
            </p>
            <p style={styles.tooltipItem}>
              總收益: <span style={{ ...styles.tooltipValue, color: '#f59e0b' }}>
                {formatCurrency(point.total_earned, currency)}
              </span>
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  // Empty state
  if (chartData.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyIcon}>📊</div>
        <h3 style={styles.emptyTitle}>資料收集中</h3>
        <p style={styles.emptyText}>
          Bot 運行後會開始收集歷史快照資料，請稍後再查看趨勢圖表。
        </p>
      </div>
    )
  }

  const timeRanges = [
    { label: '24 小時', hours: 24 },
    { label: '7 天', hours: 168 },
    { label: '30 天', hours: 720 },
  ]

  return (
    <div style={styles.container}>
      {/* Header with time range selector */}
      <div style={styles.header}>
        <h3 style={styles.title}>歷史趨勢</h3>
        <div style={styles.timeRangeButtons}>
          {timeRanges.map((range) => (
            <button
              key={range.hours}
              onClick={() => onTimeRangeChange(range.hours)}
              style={{
                ...styles.button,
                ...(timeRange === range.hours ? styles.buttonActive : styles.buttonInactive),
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Info */}
      <div style={styles.infoBar}>
        <span style={styles.infoText}>
          共 {chartData.length} 個快照 · 時間範圍: {timeRanges.find(r => r.hours === timeRange)?.label}
        </span>
      </div>

      {/* Main Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="dateLabel"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis
            yAxisId="left"
            stroke="#2563eb"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => formatCurrency(value, currency)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#8b5cf6"
            style={{ fontSize: '12px' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value) => {
              const labels: Record<string, string> = {
                wallet_balance: '錢包餘額',
                active_offers: '掛單數',
                active_loans: '貸款數',
                total_earned: '總收益',
              }
              return labels[value] || value
            }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="wallet_balance"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ fill: '#2563eb', r: 3 }}
            activeDot={{ r: 5 }}
            name="wallet_balance"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="active_offers"
            stroke="#8b5cf6"
            strokeWidth={2}
            dot={{ fill: '#8b5cf6', r: 3 }}
            activeDot={{ r: 5 }}
            name="active_offers"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="active_loans"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: '#10b981', r: 3 }}
            activeDot={{ r: 5 }}
            name="active_loans"
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="total_earned"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ fill: '#f59e0b', r: 3 }}
            activeDot={{ r: 5 }}
            name="total_earned"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>當前錢包餘額</div>
          <div style={{ ...styles.summaryValue, color: '#2563eb' }}>
            {formatCurrency(chartData[chartData.length - 1]?.wallet_balance || 0, currency)}
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>當前掛單數</div>
          <div style={{ ...styles.summaryValue, color: '#8b5cf6' }}>
            {chartData[chartData.length - 1]?.active_offers || 0}
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>當前貸款數</div>
          <div style={{ ...styles.summaryValue, color: '#10b981' }}>
            {chartData[chartData.length - 1]?.active_loans || 0}
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>總累積收益</div>
          <div style={{ ...styles.summaryValue, color: '#f59e0b' }}>
            {formatCurrency(chartData[chartData.length - 1]?.total_earned || 0, currency)}
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to format timestamp
function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month}/${day} ${hours}:${minutes}`
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  timeRangeButtons: {
    display: 'flex',
    gap: '8px',
  },
  button: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonActive: {
    backgroundColor: '#2563eb',
    color: 'white',
  },
  buttonInactive: {
    backgroundColor: '#f3f4f6',
    color: '#333',
  },
  infoBar: {
    marginBottom: '20px',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  infoText: {
    fontSize: '13px',
    color: '#666',
  },
  tooltip: {
    backgroundColor: 'white',
    padding: '12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  tooltipTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
    margin: 0,
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tooltipItem: {
    fontSize: '12px',
    color: '#666',
    margin: 0,
  },
  tooltipValue: {
    fontWeight: '600',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '24px',
  },
  summaryCard: {
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '22px',
    fontWeight: 'bold',
  },
  emptyContainer: {
    padding: '60px 20px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#666',
    maxWidth: '400px',
    margin: '0 auto',
  },
}
